import { auth } from "@/lib/firebase";
import { getPublicBottomBarPadding } from "@/components/public/PublicBottomBar";
import { ensureOwnerDoc } from "@/lib/onboardingBusiness";
import { router } from "expo-router";
import { onAuthStateChanged, type User } from "firebase/auth";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  Text,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export default function ProfileScreen() {
  const insets = useSafeAreaInsets();
  const bottomPadding = getPublicBottomBarPadding(insets.bottom);

  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [openingDashboard, setOpeningDashboard] = useState(false);

  /**
   * Prevent repeated dashboard redirects.
   * This matters because onAuthStateChanged can fire more than once.
   */
  const routedRef = useRef(false);

  const openOwnerDashboard = useCallback(async (firebaseUser: User) => {
    if (!firebaseUser?.uid || routedRef.current) {
      return;
    }

    try {
      setOpeningDashboard(true);

      /**
       * Make sure owners/{uid} exists before opening dashboard.
       * This protects owners who signed in earlier but owner doc was not created.
       */
      await ensureOwnerDoc(firebaseUser);

      routedRef.current = true;

      /**
       * Owner dashboard is now the main control center.
       * Owners create/manage businesses from there.
       */
      router.replace("/(owner)/dashboard" as any);
    } catch (error: any) {
      console.warn("Profile dashboard routing failed.");

      routedRef.current = false;

      Alert.alert(
        "Could not open dashboard",
        error?.message ||
          "You are signed in, but we could not open your owner dashboard. Please try again."
      );
    } finally {
      setOpeningDashboard(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!mounted) return;

      setUser(firebaseUser);
      setCheckingAuth(false);

      if (firebaseUser) {
        await openOwnerDashboard(firebaseUser);
      } else {
        routedRef.current = false;
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [openOwnerDashboard]);

  const displayName =
    user?.displayName || user?.email || user?.phoneNumber || "Owner";

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: "#FFFFFF",
        paddingTop: insets.top + 12,
        paddingBottom: bottomPadding,
        paddingHorizontal: 16,
      }}
    >
      <Text style={{ fontSize: 18, fontWeight: "900", color: "#111827" }}>
        Profile
      </Text>

      {checkingAuth ? (
        <View
          style={{
            marginTop: 24,
            padding: 16,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "#E5E7EB",
            backgroundColor: "#FFFFFF",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ActivityIndicator />

          <Text
            style={{
              marginTop: 10,
              fontWeight: "800",
              color: "#6B7280",
            }}
          >
            Checking your account...
          </Text>
        </View>
      ) : user ? (
        <View
          style={{
            marginTop: 24,
            padding: 16,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "#E5E7EB",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Text
            style={{
              fontWeight: "700",
              color: "#6B7280",
            }}
          >
            Signed in as
          </Text>

          <Text
            style={{
              marginTop: 4,
              fontSize: 16,
              fontWeight: "900",
              color: "#111827",
            }}
            numberOfLines={1}
          >
            {displayName}
          </Text>

          <View
            style={{
              marginTop: 18,
              minHeight: 56,
              borderRadius: 14,
              backgroundColor: "#F9FAFB",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 14,
            }}
          >
            {openingDashboard ? <ActivityIndicator /> : null}

            <Text
              style={{
                marginTop: openingDashboard ? 8 : 0,
                fontWeight: "900",
                color: "#111827",
                textAlign: "center",
              }}
            >
              Opening Owner Dashboard...
            </Text>

            <Text
              style={{
                marginTop: 4,
                fontSize: 12,
                fontWeight: "700",
                color: "#6B7280",
                textAlign: "center",
              }}
            >
              Manage or create your business inside the dashboard.
            </Text>
          </View>
        </View>
      ) : (
        <View
          style={{
            marginTop: 24,
            padding: 16,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "#E5E7EB",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Text
            style={{
              fontSize: 16,
              fontWeight: "900",
              color: "#111827",
            }}
          >
            Business Owner
          </Text>

          <Text
            style={{
              marginTop: 6,
              fontWeight: "700",
              color: "#6B7280",
              lineHeight: 20,
            }}
          >
            Sign in to create or manage your Soko Grid Smart Business Card.
          </Text>

          <Pressable
            onPress={() => router.push("/(owner)/onboarding/signin" as any)}
            style={{
              marginTop: 16,
              height: 46,
              borderRadius: 14,
              backgroundColor: "#111827",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text style={{ color: "#FFFFFF", fontWeight: "900" }}>
              Sign in to continue
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}
